import React, { cloneElement, useEffect } from "react";
import {
  useInput,
  useReferenceInputController,
  ListContextProvider,
  ResourceContextProvider,
} from "ra-core";

// Minimal sanitize: just drop a few controller-specific props
const sanitizeRestProps = ({
  dataStatus = null,
  enableGetChoices = null,
  filter = null,
  filterToQuery = null,
  onChange = null,
  perPage = null,
  reference = null,
  referenceRecord = null,
  referenceSource = null,
  sort = null,
  validation = null,
  ...rest
}) => rest;

export const StoreReferenceInput = (props) => {
  const { format, onBlur, onChange, onFocus, parse, validate, ...rest } = props;

  const inputProps = useInput({
    format,
    onBlur,
    onChange,
    onFocus,
    parse,
    validate,
    ...rest,
  });

  const controllerProps = useReferenceInputController({
    ...rest,
    ...inputProps,
  });

  return (
    <StoreReferenceInputView {...inputProps} {...rest} {...controllerProps} />
  );
};

const StoreReferenceInputView = (props) => {
  const {
    children,
    choices,
    error,
    helperText,
    input,
    meta,
    possibleValues,
    reference,
    resource,
    source,
    warning,
    ...rest
  } = props;

  useEffect(() => {
    if (
      reference === "stores" &&
      source === "store_id" &&
      input?.value &&
      Array.isArray(choices) &&
      choices.length > 0 &&
      !choices.some((choice) => choice.id === input.value)
    ) {
      // Log and clear invalid store id from the form value
      // so the record does not keep a non-existing store_id.
      // eslint-disable-next-line no-console
      console.warn(
        `Store with id ${input.value} not found in choices. Clearing value.`,
      );
      input.onChange(null);
    }
  }, [choices, input, reference, source]);

  if (React.Children.count(children) !== 1) {
    throw new Error("<StoreReferenceInput> only accepts a single child");
  }

  if (error) {
    // Basic error rendering to avoid pulling internal MUI components
    return (
      <span style={{ color: "red" }}>
        {rest.label || "Error"}: {error.message || String(error)}
      </span>
    );
  }

  const finalMeta = warning
    ? {
        ...meta,
        error: warning,
      }
    : meta;

  const disabledHelperText = helperText === false ? { helperText } : {};

  return (
    <ResourceContextProvider value={reference}>
      <ListContextProvider value={possibleValues}>
        {cloneElement(children, {
          input,
          resource,
          source,
          choices,
          meta: finalMeta,
          ...disabledHelperText,
          ...sanitizeRestProps(rest),
        })}
      </ListContextProvider>
    </ResourceContextProvider>
  );
};

export default StoreReferenceInput;
